import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { GraduationCap, PlayCircle, FileText, CheckCircle2, ListChecks, BarChart3, BookOpen, Check, Crown } from 'lucide-react'
import { courseApi, type CourseProgress, type CourseRating } from '../api/lessonBuilderApi'
import { COURSE_LEVELS, type CourseDoc } from '../types/CourseDoc'
import StarRating from '../components/StarRating'

const levelLabel = (lv?: string) => COURSE_LEVELS.find((x) => x.value === lv)?.label || ''

// Trang khoá học công khai (giao diện học sinh): bìa + giới thiệu + lộ trình bài học.
const CourseViewerPage: React.FC = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [course, setCourse] = useState<CourseDoc | null | undefined>(undefined)
  const [progress, setProgress] = useState<CourseProgress | null>(null)
  const [enrolling, setEnrolling] = useState(false)
  const [rating, setRating] = useState<CourseRating | null>(null)

  const loadProgress = (courseId: string) => courseApi.progress(courseId).then(setProgress).catch(() => {})

  useEffect(() => {
    if (!slug) return
    courseApi.getBySlug(slug).then((c) => {
      setCourse(c)
      if (c) {
        loadProgress(c.id)
        courseApi.getRating(c.id).then(setRating).catch(() => {})
      }
    }).catch(() => setCourse(null))
  }, [slug])

  const handleRate = async (stars: number) => {
    if (!course) return
    try {
      setRating(await courseApi.rate(course.id, stars))
    } catch {
      alert('Cần đăng nhập để đánh giá khoá học.')
    }
  }

  const handleEnroll = async () => {
    if (!course) return
    setEnrolling(true)
    try {
      await courseApi.enroll(course.id)
      await loadProgress(course.id)
    } catch (e) {
      const msg = (e as Error)?.message || ''
      alert(msg.includes('403') || msg.toLowerCase().includes('pro')
        ? 'Khoá học này yêu cầu tài khoản PRO. Vui lòng nâng cấp để ghi danh.'
        : 'Ghi danh chưa thành công. Hãy đăng nhập rồi thử lại.')
    } finally {
      setEnrolling(false)
    }
  }

  if (course === undefined) return <div className="p-10 text-center text-gray-400">Đang tải...</div>
  if (!course) {
    return (
      <div className="p-10 text-center">
        <p className="text-gray-500 mb-4">Không tìm thấy khoá học.</p>
        <Link to="/teacher/khoa-hoc" className="text-blue-600 font-semibold">← Về danh sách khoá học</Link>
      </div>
    )
  }

  const lessons = course.lessonSummaries || []
  const totalCards = lessons.reduce((s, l) => s + (l.cardCount || 0), 0)
  const outcomes = course.outcomes || []
  const requirements = course.requirements || []
  const doneIds = new Set((progress?.lessons || []).filter((x) => x.completed).map((x) => x.id))
  const ratAvg = rating?.avg ?? course.ratingAvg ?? 0
  const ratCount = rating?.count ?? course.ratingCount ?? 0

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-5xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 flex-wrap text-blue-100 text-sm font-semibold mb-3">
              <span className="flex items-center gap-1"><GraduationCap size={18} /> Khoá học</span>
              {course.category && (
                <span className="px-2 py-0.5 rounded-full bg-white/15 text-white text-xs">{course.category}</span>
              )}
              {levelLabel(course.level) && (
                <span className="px-2 py-0.5 rounded-full bg-white/15 text-white text-xs flex items-center gap-1">
                  <BarChart3 size={12} /> {levelLabel(course.level)}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 ${
                course.access === 'pro' ? 'bg-amber-400 text-amber-900' : 'bg-emerald-400/90 text-emerald-950'
              }`}>
                {course.access === 'pro' ? <><Crown size={12} /> PRO</> : 'Miễn phí'}
              </span>
            </div>
            <h1 className="text-3xl font-black mb-2">{course.title}</h1>
            {course.subtitle && <p className="text-lg text-blue-100 mb-3">{course.subtitle}</p>}
            {ratCount > 0 && (
              <div className="flex items-center gap-2 text-blue-100 text-sm">
                <StarRating value={ratAvg} size={18} />
                <span>{ratAvg.toFixed(1)} · {ratCount} đánh giá</span>
              </div>
            )}
            <div className="flex items-center gap-4 text-blue-200 text-sm mt-4">
              <span className="flex items-center gap-1"><BookOpen size={15} /> {lessons.length} bài giảng</span>
              <span className="flex items-center gap-1"><FileText size={15} /> {totalCards} mục nội dung</span>
            </div>
          </div>
          {course.coverImage && (
            <div className="lg:col-span-1">
              <img src={course.coverImage} alt={course.title} className="w-full h-44 object-cover rounded-2xl shadow-lg ring-4 ring-white/20" />
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cột chính: giới thiệu + lộ trình */}
        <div className="lg:col-span-2 space-y-6">
          {course.description && (
            <section className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-lg font-black text-gray-900 mb-3">Giới thiệu khoá học</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{course.description}</p>
            </section>
          )}

          {outcomes.length > 0 && (
            <section className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="flex items-center gap-2 text-lg font-black text-gray-900 mb-4">
                <ListChecks size={20} className="text-emerald-600" /> Bạn sẽ học được gì
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {outcomes.map((o, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span>{o}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Lộ trình bài học */}
          <section className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-lg font-black text-gray-900 mb-4">Nội dung khoá học</h2>
            {lessons.length === 0 ? (
              <p className="text-center text-gray-400 py-6">Khoá học chưa có bài giảng.</p>
            ) : (
              <div className="space-y-3">
                {lessons.map((l, i) => {
                  const done = doneIds.has(l.id)
                  return (
                  <button
                    key={l.id}
                    onClick={() => navigate(`/bai-giang/${l.slug}`)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                      done ? 'bg-emerald-50/50 border-emerald-100 hover:border-emerald-300' : 'bg-gray-50/60 border-gray-100 hover:border-blue-200 hover:bg-blue-50/40'
                    }`}
                  >
                    <span className={`w-9 h-9 rounded-full font-bold flex items-center justify-center shrink-0 ${
                      done ? 'bg-emerald-500 text-white' : 'bg-blue-50 text-blue-600'
                    }`}>
                      {done ? <Check size={18} /> : i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 truncate">{l.title}</p>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <FileText size={12} /> {l.cardCount} mục nội dung{done ? ' · đã hoàn thành' : ''}
                      </p>
                    </div>
                    {done ? <CheckCircle2 className="text-emerald-500 shrink-0" size={22} /> : <PlayCircle className="text-blue-500 shrink-0" size={22} />}
                  </button>
                  )
                })}
              </div>
            )}
          </section>
        </div>

        {/* Cột phụ: yêu cầu + bắt đầu học */}
        <aside className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 lg:sticky lg:top-6">
            {/* Thanh tiến độ khi đã ghi danh */}
            {progress?.enrolled && progress.total > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs font-bold text-gray-500 mb-1">
                  <span>Tiến độ của bạn</span>
                  <span className="text-emerald-600">{progress.completed}/{progress.total} bài · {progress.percent}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${progress.percent}%` }} />
                </div>
              </div>
            )}

            {progress?.enrolled ? (
              <button
                disabled={lessons.length === 0}
                onClick={() => navigate(`/bai-giang/${progress.nextSlug || lessons[0]?.slug}`)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-40"
              >
                <PlayCircle size={20} /> {progress.percent >= 100 ? 'Học lại' : progress.completed > 0 ? 'Học tiếp' : 'Bắt đầu học'}
              </button>
            ) : progress ? (
              <button
                disabled={lessons.length === 0 || enrolling}
                onClick={handleEnroll}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-40"
              >
                <GraduationCap size={20} /> {enrolling ? 'Đang ghi danh...' : 'Ghi danh học'}
              </button>
            ) : (
              <button
                disabled={lessons.length === 0}
                onClick={() => lessons[0] && navigate(`/bai-giang/${lessons[0].slug}`)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-40"
              >
                <PlayCircle size={20} /> Bắt đầu học
              </button>
            )}
            <div className="mt-4 space-y-2 text-sm text-gray-600">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Số bài giảng</span>
                <span className="font-bold text-gray-800">{lessons.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Mục nội dung</span>
                <span className="font-bold text-gray-800">{totalCards}</span>
              </div>
              {levelLabel(course.level) && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Trình độ</span>
                  <span className="font-bold text-gray-800">{levelLabel(course.level)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Đánh giá sao */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-base font-black text-gray-900 mb-2">Đánh giá</h2>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl font-black text-gray-900">{ratAvg > 0 ? ratAvg.toFixed(1) : '—'}</span>
              <StarRating value={ratAvg} count={ratCount} />
            </div>
            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs text-gray-500 mb-1">{rating?.myStars ? `Bạn đã đánh giá ${rating.myStars} sao` : 'Đánh giá của bạn:'}</p>
              <StarRating value={rating?.myStars || 0} size={24} onRate={handleRate} />
            </div>
          </div>

          {(course.instructorName || course.instructorSchool || course.instructorExperience) && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-base font-black text-gray-900 mb-3">Giảng viên</h2>
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-black text-lg shrink-0">
                  {(course.instructorName || 'GV').charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  {course.instructorName && <p className="font-bold text-gray-900">{course.instructorName}</p>}
                  {course.instructorSchool && <p className="text-xs text-gray-500">{course.instructorSchool}</p>}
                  {course.instructorExperience && <p className="text-sm text-gray-600 mt-1.5 leading-relaxed whitespace-pre-line">{course.instructorExperience}</p>}
                </div>
              </div>
            </div>
          )}

          {requirements.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-base font-black text-gray-900 mb-3">Yêu cầu đầu vào</h2>
              <ul className="space-y-2">
                {requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0 mt-1.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

export default CourseViewerPage
